import { useState } from "react";
import { useEffect } from "react";



type SelectedKey =
  | "/img/overviewsection/manufacturing_facility_circle.png"
  | "/img/overviewsection/research_and_development_circle.png"
  | "custom_circle";

const data: Record<
  SelectedKey,
  { title: string; description: string; rightImage: string }
> = {
  "/img/overviewsection/manufacturing_facility_circle.png": {
    title: "Manufacturing Facility",
    description: `Our zero-discharge, zero-emission manufacturing facility in India is designed with sustainability and precision at its core. The facility features dedicated fermentation trains for each product class, supported by advanced PLC and SCADA systems for full process control and monitoring. With both solid-state and submerged fermentation capabilities, we ensure optimal conditions for consistent product quality.`,
    rightImage: "/img/overviewsection/manufacturing_facility.png",
  },
  "/img/overviewsection/research_and_development_circle.png": {
    title: "Research & Development",
    description: `We are deeply committed to persistent, purpose-driven research, which
      forms a core pillar of our mission. Our fully equipped, independent
      in-house R&D center, supported by a pilot plant, is designed to handle
      a wide range of probiotics and biotech molecules. The facility is led
      by a skilled team of microbiology and biotechnology experts, along
      with external specialists from various application areas.`,
    rightImage: "/img/overviewsection/research_and_development.png",
  },
  "custom_circle": {
    title: "Custom Section",
    description: "This is a custom section description.",
    rightImage: "/img/overviewsection/custom_image.png",
  },
};

const manufacturingPopupContent = (
  <>
    <h2 className="bg-white text-black text-3xl font-bold p-4 mb-2">
      Manufacturing facility
    </h2>
    <div className="text-black text-base space-y-2 mb-4 text-justify">
      <p>
        Our zero-discharge, zero-emission manufacturing facility in India is designed with sustainability and precision at its core. The facility features dedicated fermentation trains for each product class, supported by advanced PLC and SCADA systems for full process control and monitoring. With both solid-state and submerged fermentation capabilities, we ensure optimal conditions for consistent product quality.
      </p>
      <p>
        Our upstream infrastructure includes aerobic and anaerobic fermentation under various parameters, while the downstream processes are equipped with high-speed centrifugation, ultra-filtration, spray drying, lyophilization, encapsulation, and automated blending and packaging. These integrated systems enable us to produce a wide range of formulations in various forms and pack sizes.
      </p>
      <p>
        We maintain complete control over the entire production cycle, allowing us to deliver end-to-end solutions tailored to client needs. Our commitment to continuous technological upgrades ensures that our facility remains state-of-the-art, capable of adapting to evolving market demands and customer expectations.
      </p>
    </div>
    <img
      src="/img/overviewsection/popup_manufacturing.png"
      alt="Manufacturing Facility"
      className="w-full h-auto rounded"
    />
  </>
);

const researchPopupContent = (
  <>
    <h2 className="bg-white text-black text-3xl font-bold p-4 mb-2">
      Research & Development
    </h2>
    <div className="text-black text-base space-y-4 mb-4 whitespace-pre-line text-justify">
     <p>
      We are deeply committed to persistent, purpose-driven research, which forms a core pillar of our mission. Our fully equipped, independent in-house R&D center, supported by a pilot plant, is designed to handle a wide range of probiotics and biotech molecules. The facility is led by a skilled team of microbiology and biotechnology experts, along with external specialists from various application areas.
     </p>
     <p>
      Our R&D efforts focus on enhancing existing product lines through new strain identification, characterization, and host response studies. We explore probiotic action mechanisms, synergies between strains, and innovations in formulation, dosing, and delivery. We also work on improving yields and discovering new application areas where probiotics can offer clear benefits.
    </p>
    <p>
      Beyond probiotics, we are focused on the development of next-generation biotech products such as nutraceuticals, biotherapeutics, and peptides. From research and pilot-scale production to commercialization, our R&D pipeline is driven by customer needs and aligned with our vision of advancing sustainable and effective biotech solutions.
    </p>
    </div>
    <img
      src="/img/overviewsection/popup_reasearch.png"
      alt="Research & Development"
      className="w-full h-auto rounded"
    />
  </>
);

const Manufacturing = () => {

  const [selected, setSelected] = useState<SelectedKey>(
    "/img/overviewsection/research_and_development_circle.png"
  );
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    if (isModalOpen) {
      document.body.style.overflow = "hidden"; // disable background scroll
    } else {
      document.body.style.overflow = ""; // restore
    }

    return () => {
      document.body.style.overflow = ""; // cleanup if component unmounts
    };
  }, [isModalOpen]);

  const handleSelect = (imgSrc: SelectedKey) => {
    setSelected(imgSrc);
  };

  const handleReadMore = () => {
    if (
      selected === "/img/overviewsection/manufacturing_facility_circle.png" ||
      selected === "/img/overviewsection/research_and_development_circle.png"
    ) {
      setIsModalOpen(true);
    }
  };

  const closeModal = () => setIsModalOpen(false);

  return (
    <>
      <section className="flex flex-col lg:flex-row w-full min-h-screen relative">

        <div className="relative w-full lg:w-[48%] h-[500px] lg:h-auto">
          <img
            key={data[selected].rightImage}
            src={data[selected].rightImage}
            alt={data[selected].title}
            className="w-full h-full object-cover"
          />
        </div>

        <div className="flex lg:hidden justify-center gap-6 absolute w-full px-4 top-[36%] left-0 z-10">
          <img
            src={
              selected === "/img/overviewsection/research_and_development_circle.png"
                ? "/img/overviewsection/manufacturing_facility_circle_dull.png"
                : "/img/overviewsection/manufacturing_facility_circle.png"
            }
            alt="Manufacturing Facility"
            onClick={() =>
              handleSelect("/img/overviewsection/manufacturing_facility_circle.png")
            }
            className={`w-30 h-30 rounded-full object-cover cursor-pointer ${
              selected === "/img/overviewsection/manufacturing_facility_circle.png"
                ? "border-4 border-red-500"
                : selected === "/img/overviewsection/research_and_development_circle.png"
                ? "" // no border for dull
                : "border-4 border-white"
            }`}
          />
          <img
            src={
              selected === "/img/overviewsection/manufacturing_facility_circle.png"
                ? "/img/overviewsection/research_and_development_circle_dull.png"
                : "/img/overviewsection/research_and_development_circle.png"
            }
            alt="Research and Development"
            onClick={() =>
              handleSelect("/img/overviewsection/research_and_development_circle.png")
            }
            className={`w-30 h-30 rounded-full object-cover cursor-pointer ${
              selected === "/img/overviewsection/research_and_development_circle.png"
                ? "border-4 border-red-500"
                : selected === "/img/overviewsection/manufacturing_facility_circle.png"
                ? "" // no border for dull
                : "border-4 border-white"
            }`}
          />
        </div>

        <div className="relative w-full lg:w-[52%] bg-[#1c2c38] text-white flex flex-col justify-start px-14 lg:px-28 py-12">
          <h2 className="text-4xl font-bold mb-4 mt-20">{data[selected].title}</h2>
          <p className="text-gray-200 leading-relaxed mb-10 mt-10 text-justify">
            {data[selected].description}
          </p>

          <button
            onClick={handleReadMore}
            className="bg-[#ea4b20] text-white font-semibold px-6 py-3 rounded-full hover:bg-[#c63c19] transition w-fit mt-6 cursor-pointer"
          >
            Read More
          </button>

          <div className="hidden lg:block absolute top-1/4 left-[-60px] cursor-pointer">
            <img
              src={
                selected === "/img/overviewsection/research_and_development_circle.png"
                  ? "/img/overviewsection/manufacturing_facility_circle_dull.png"
                  : "/img/overviewsection/manufacturing_facility_circle.png"
              }
              alt="Manufacturing Facility"
              onClick={() =>
                handleSelect("/img/overviewsection/manufacturing_facility_circle.png")
              }
              className={`w-28 h-28 rounded-full object-cover mb-8 ${
                selected === "/img/overviewsection/manufacturing_facility_circle.png"
                  ? "border-4 border-red-500"
                  : selected === "/img/overviewsection/research_and_development_circle.png"
                  ? "" // no border for dull
                  : "border-4 border-white"
              }`}
            />
            <img
              src={
                selected === "/img/overviewsection/manufacturing_facility_circle.png"
                  ? "/img/overviewsection/research_and_development_circle_dull.png"
                  : "/img/overviewsection/research_and_development_circle.png"
              }
              alt="Research and Development"
              onClick={() =>
                handleSelect("/img/overviewsection/research_and_development_circle.png")
              }
              className={`w-28 h-28 rounded-full object-cover mb-8 ${
                selected === "/img/overviewsection/research_and_development_circle.png"
                  ? "border-4 border-red-500"
                  : selected === "/img/overviewsection/manufacturing_facility_circle.png"
                  ? "" // no border for dull
                  : "border-4 border-white"
              }`}
            />
          </div>
        </div>
        <div className="hidden lg:block absolute h-[130px] w-[130px] bottom-7 right-50 float-animation">
          <div
            onClick={() => handleSelect("custom_circle")}
            className="w-28 h-28 rounded-full"
            style={{ backgroundColor: "rgba(232, 82, 34, 1)" }}
          />
        </div>
      </section>

      {/* Modal */}
      {isModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/50"
          onClick={closeModal}
        >
          <div
            className="bg-white rounded-lg max-w-3xl w-full p-8 relative max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()} 
          >
            <button
              onClick={closeModal}
              className="absolute top-4 right-4 text-black font-bold text-2xl cursor-pointer"
              aria-label="Close Modal"
            >
              &times;
            </button>

            {selected === "/img/overviewsection/manufacturing_facility_circle.png"
              ? manufacturingPopupContent
              : selected === "/img/overviewsection/research_and_development_circle.png"
              ? researchPopupContent
              : null}
          </div>
        </div>
      )}
    </>
  );
};

export default Manufacturing;
